import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (provider === "paypal") {
      refresh().then(() => setStatus("paid"));
      return;
    }
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      if (n > 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { await refresh(); setStatus("paid"); return; }
      } catch { /* retry */ }
      setAttempts(n);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId, provider]);

  return (
    <div className="max-w-lg mx-auto px-5 py-24 text-center paper-noise min-h-[70vh]">
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-[#E06D53] mx-auto mb-6 animate-spin" />
          <h1 className="font-serif text-3xl tracking-tight text-[#2C2A28]">{t("payment.checking")}</h1>
          <p className="text-[#5C5854] mt-3">{t("payment.wait")}{attempts ? ` (${attempts})` : ""}.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-[#2D6A4F] mx-auto mb-6" data-testid="payment-success-icon" />
          <h1 className="font-serif text-4xl tracking-tight text-[#2C2A28]">{t("payment.confirmed")}</h1>
          <p className="text-[#5C5854] mt-3">{t("payment.confirmedDesc")}</p>
          <div className="flex gap-3 justify-center mt-8">
            <Button onClick={() => navigate("/pays")} className="rounded-full bg-[#E06D53] hover:bg-[#C95B42] text-white" data-testid="success-explore">{t("payment.explore")}</Button>
            <Button onClick={() => navigate("/dashboard")} variant="outline" className="rounded-full">{t("payment.dashboard")}</Button>
          </div>
        </>
      )}
      {(status === "timeout" || status === "error") && (
        <>
          <XCircle className="w-14 h-14 text-[#E06D53] mx-auto mb-6" />
          <h1 className="font-serif text-3xl tracking-tight text-[#2C2A28]">{t("payment.verifying")}</h1>
          <p className="text-[#5C5854] mt-3">{t("payment.verifyingDesc")}</p>
          <Link to="/dashboard"><Button className="rounded-full mt-6 bg-[#E06D53] text-white">{t("payment.dashboard")}</Button></Link>
        </>
      )}
    </div>
  );
}
